import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild, afterNextRender, effect, inject, input, output, DOCUMENT } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { DynamicHeaderCommon } from 'src/app/components/dynamic-header/category/dynamic-header-common.class';
import { DynamicMenuCollapseComponent } from 'src/app/components/dynamic-menu-collapse/dynamic-menu-collapse.component';
import { SearchBarComponent } from 'src/app/components/search-suggestion/components/search-bar/search-bar.component';
import { StaticMenuCollapseComponent } from 'src/app/components/static-menu-collapse/static-menu-collapse.component';
import { IMAGE_URLS } from 'src/app/core/constants/image-source';
import { ClickOutsideDirective } from 'src/app/core/dynamic-html-directives/click-outside.directive';
import { BrowserApiService } from 'src/app/core/services/browser-api/browser-api.service';
import { Cart } from 'src/app/core/services/cart/cart-api.service';
import { DeviceDetectionService } from 'src/app/core/services/device-detection';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { safeCartSetter } from 'src/app/core/utilities/cart-fallback.util';
import { createDefaultCart, createDefaultHTMLElement, createDefaultHeaderData } from 'src/app/core/utilities/type-defaults.util';
import { Category } from 'src/app/shared/services/common-api.service';
import { MenuCollapseService } from '../../services/menu-collapse.service';
import { HeaderData } from '../header-data';

@Component({
  selector: 'category-layout-13',
  standalone: true,
  templateUrl: './layout-13.component.html',
  styleUrls: ['./layout-13.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    SearchBarComponent,
    StaticMenuCollapseComponent,
    DynamicMenuCollapseComponent,
    ClickOutsideDirective
  ]
})
export class Layout13Component extends DynamicHeaderCommon implements OnInit, AfterViewInit, OnDestroy {
  protected override userService = inject(UserService);
  private readonly deviceDetectionService = inject(DeviceDetectionService);
  private readonly router = inject(Router);

  IMAGE_URLS = IMAGE_URLS;
  UTM_MEDIUM = 'menu';
  private hoverTimeoutId: ReturnType<typeof setTimeout> | null = null;

  @ViewChild('changeHeaderElement') declare changeHeaderElement: ElementRef;
  @ViewChild('changeHeaderMobileElement') declare changeHeaderMobileElement: ElementRef;

  readonly dynamicHeaderInline = input<boolean>(false);
  readonly dynamicHeaderHover = input<boolean>(false);
  readonly domain = input<string>('');
  readonly layoutType = input<string>('');

  readonly headerDataInput = input<HeaderData>(createDefaultHeaderData());

  // Getters for backward compatibility
  override get headerData(): HeaderData {
    return this._headerData;
  }
  protected declare _headerData: HeaderData;

  // Expose service signals for template usage
  get isDesktop() {
    return this.deviceDetectionService.isDesktop;
  }
  get isMobile() {
    return this.deviceDetectionService.isMobile;
  }
  get isTablet() {
    return this.deviceDetectionService.isTablet;
  }
  get deviceType() {
    return this.deviceDetectionService.deviceType;
  }

  // Internal setter for parent class compatibility
  override set selectedDeviceType(value: string) {
    super.selectedDeviceType = value;
  }
  override get selectedDeviceType(): string {
    return super.selectedDeviceType;
  }

  readonly categoryInput = input<Category[] | null>(null);
  readonly clickedElementAvailableInput = input<HTMLElement | null>(null);
  readonly cartInput = input<Cart | null>(null);

  // Getters for backward compatibility
  get category(): Category[] | null {
    return this._category;
  }
  get clickedElementAvailable(): HTMLElement {
    return this._clickedElementAvailable;
  }

  private _category: Category[] | null = null;
  private _clickedElementAvailable: HTMLElement = createDefaultHTMLElement() as HTMLElement;
  get cart(): Cart {
    return this._cart;
  }
  private _cart: Cart = createDefaultCart();

  isSubmenuAvailable = true;
  override showSubcategoryPanel = false;
  override searchInputControl = new FormControl<string | null>('');

  // Update output to use Angular 20 output() function
  categoryHeaderStyle$ = output<string>();

  // NEW: Desktop and mobile hamburger menu states
  isDesktopHamburgerMenu = false;
  isMobileHamburgerMenu = false;

  constructor() {
    const browserApiService = inject(BrowserApiService);

    super(
      inject(Renderer2),
      inject(MenuCollapseService),
      inject(ElementRef),
      inject(PlatformService),
      inject<Document>(DOCUMENT),
      browserApiService
    );

    // Effect to handle device type changes and business logic
    effect(() => {
      const deviceInfo = this.deviceDetectionService.getLayoutBusinessInfo('layout-13');

      // Handle hamburger menu logic
      if (deviceInfo.shouldCloseHamburgerMenu) {
        this.isHamburgerMenu = false;
        this.menuCollapseService.setHamburgerMenu(false);
        this.showMobileSearchPanel = false;
        // Close desktop and mobile menus when switching to desktop
        this.isDesktopHamburgerMenu = false;
        this.isMobileHamburgerMenu = false;
      }

      // Handle mobile search panel
      if (deviceInfo.shouldHideMobileSearchPanel) {
        this.showMobileSearchPanel = false;
      }

      // Handle repositioning
      if (deviceInfo.needsRepositioning) {
        this.repositionHeaderMenu();
      }

      // Update parent class using the setter
      if (deviceInfo.type) {
        const deviceTypeString = deviceInfo.type.toString();
        this.selectedDeviceType = deviceTypeString;
      }
    });

    effect(() => {
      const headerDataValue = this.headerDataInput();
      this._headerData = headerDataValue;
      this.addHoverForMenu();
    });

    effect(() => {
      const cartValue = this.cartInput();
      this._cart = safeCartSetter(cartValue);
    });

    effect(() => {
      const categoryValue = this.categoryInput();
      if (categoryValue) {
        this._category = categoryValue;

        // When category data is available, attach hover listeners
        if (categoryValue.length > 0) {
          afterNextRender(() => {
            this.addHoverForMenu();
          });
        }
      } else {
        this._category = null;
      }
    });

    effect(() => {
      const clickedElementValue = this.clickedElementAvailableInput();
      if (clickedElementValue) {
        this._clickedElementAvailable = clickedElementValue;
      } else {
        this._clickedElementAvailable = createDefaultHTMLElement() as HTMLElement;
      }
    });
  }

  ngOnInit(): void {
    if (this.platformService.isBrowser()) {
      // Refresh user data from the base class
      this.refreshUserInChild();
      // ✅ REMOVED: setTimeout - now handled by categoryInput effect in constructor
    }
  }

  ngAfterViewInit(): void {
    if (this.platformService.isBrowser()) {
      this.repositionHeaderMenu();
      this.hoverTimeoutId = setTimeout(() => {
        this.addHoverForMenu();
      }, 2000);
      this.applyFocusEvent();
    }
  }

  onLinkClick(event?: Event): void {
    const subMenus = document.getElementsByClassName("hide-sub-menu-header");
    // Iterate over the elements and set display to none
    Array.from(subMenus).forEach(subMenu => {
      this.renderer.setStyle(subMenu, 'display', 'none');
    });
  }

  filterProductsByCategory(category_url_path: string): void {
    this.router.navigate([`/category/${category_url_path ?? ''}`], {
      queryParams: {
        utm_source: 'menu',
        utm_medium: category_url_path
      }
    });
  }

  override toggleSearchDropDown(): void {
    this.isSearchOverlay = !this.isSearchOverlay;
    this.isHamburgerMenu = false;
    this.searchInputControl.reset();
    // Also close desktop and mobile menus when opening search
    this.isDesktopHamburgerMenu = false;
    this.isMobileHamburgerMenu = false;

    if (this.isSearchOverlay) {
      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
    } else {
      this.renderer.removeStyle(this.document.body, 'overflow');
    }
  }

  override toggleHamburgerMenu(): void {
    this.isToggled = true;
    this.menuCollapseService.toggleHamburgerMenu();
    this.isHamburgerMenu = !this.isHamburgerMenu;
    this.isSearchOverlay = false;
    // Also close desktop menu when opening mobile menu
    this.isDesktopHamburgerMenu = false;
    if (this.isHamburgerMenu) {
      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
    } else {
      this.renderer.removeStyle(this.document.body, 'overflow');
    }
  }

  // NEW: Desktop hamburger menu toggle method
  toggleDesktopHamburgerMenu(): void {
    this.isDesktopHamburgerMenu = !this.isDesktopHamburgerMenu;

    // Close mobile menu if open
    this.isMobileHamburgerMenu = false;
    this.isHamburgerMenu = false;
    // Close search overlay if open
    this.isSearchOverlay = false;
    if (this.isDesktopHamburgerMenu) {
      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
    } else {
      this.renderer.removeStyle(this.document.body, 'overflow');
    }
  }

  // NEW: Mobile hamburger menu toggle method
  toggleMobileHamburgerMenu(): void {
    this.isMobileHamburgerMenu = !this.isMobileHamburgerMenu;
    // Close desktop menu if open
    this.isDesktopHamburgerMenu = false;
    this.isHamburgerMenu = false;
    // Close search overlay if open
    this.isSearchOverlay = false;
    if (this.isMobileHamburgerMenu) {
      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
    } else {
      this.renderer.removeStyle(this.document.body, 'overflow');
    }
  }

  searchItems(): void {
    this.toggleSearchDropDown();
  }

  subMenu(): void {
    this.isSubmenuAvailable = true;
  }

  toggleMenu(): void {
    this.showSubcategoryPanel = !this.showSubcategoryPanel;
  }

  openSubcategoryPanel(sub_categories: Category[] = []): void {
    if (sub_categories?.length) {
      this.showSubcategoryPanel = !this.showSubcategoryPanel;
      this.mobileSubCategory = sub_categories;
    }
  }

  // Implement searchProduct method to handle search input events
  override searchProduct(searchQuery: { keyboardEvent: KeyboardEvent, suggestionItem: any, searchInputValue?: string }): void {
    const { keyboardEvent, suggestionItem } = searchQuery;

    if (suggestionItem) {
      this.searchQuery$.emit(searchQuery);
      this.disableSearchPanel(); // Close panel after suggestion selection
    } else if (keyboardEvent) {
      if (keyboardEvent.key === 'Enter') {
        this.searchQuery$.emit({ ...searchQuery, searchInputValue: this.searchInputControl?.value || '' });
        this.disableSearchPanel(); // Close panel after Enter key press
      }
    }
  }

  ngOnDestroy(): void {
    if (this.hoverTimeoutId) {
      clearTimeout(this.hoverTimeoutId);
    }
  }
}
